// Org switcher. The intranet is an org-level product (MODEL.md inv 15):
// each team-org has one "home" workspace (`type:'shared'`, aka the HQ
// workspace) that backs chat, tickets, docs, meet, KB, etc. Workspaces
// exist for billing boundaries in the editor — not for collab scoping.
//
// This component lets the user pick which org's intranet to view.
// Activation goes through `el.sdk.switchWorkspace(homeWsId)` — the SDK
// owns the federation/refresh round-trip; the frontend never reaches
// directly into sdk-bridge. RLS picks up the new active_workspace_id
// from the refreshed JWT and re-scopes every tenant-table query.
//
// Renders whenever the user belongs to at least one team org. A "+ Create
// organization" footer link is always present.
//
// NOTE: the component name is kept as AppWorkspaceSwitcher for
// backwards compatibility with callers (IntranetAppHeader +
// CanvasAppHeader). It's an org switcher now.

// Module-level guard timer: cleared by app.js's onWorkspaceChange when the
// claims-change path fires successfully. If it doesn't fire within 5 s the
// timer falls back to a full reload so the overlay never hangs.
let _wsFallbackTimer = null

// Called by app.js's _onWorkspaceChange after the in-place data refresh
// completes. Prevents the 5 s fallback from triggering a redundant reload.
export const clearWorkspaceSwitchFallback = () => {
  if (_wsFallbackTimer) {
    clearTimeout(_wsFallbackTimer)
    _wsFallbackTimer = null
  }
}

const _orgs = (root) => {
  const raw = root?.jwtClaims?.orgs
  return Array.isArray(raw) ? raw : []
}

const _workspaces = (root) => {
  const raw = root?.jwtClaims?.workspaces
  if (!Array.isArray(raw)) return []
  return raw
}

const _activeWsId = (root) => root?.jwtClaims?.active_workspace_id || ''

// Find the home workspace for an org. Preference order:
//   1. type === 'shared' (server convention for team/HQ workspaces)
//   2. type === 'hq' (reserved if/when server adds it)
//   3. first workspace in that org by claim order
const _homeWorkspace = (org, root) => {
  const wss = _workspaces(root).filter((w) => (w.org_id || w.organization) === org.id)
  return (
    wss.find((w) => w.type === 'hq') ||
    wss.find((w) => w.type === 'shared') ||
    wss[0] ||
    null
  )
}

// Returns the org the UI should treat as active. Prefers the optimistic
// `_pendingActiveOrgId` (set the moment the user picks an org) over the
// JWT-derived id (which only updates after a server round-trip + JWT
// refresh — easily 1–5 s of staleness). Falls through to the JWT-active
// workspace's org when no pending pick is in flight, then to the first
// org in the claim list as a last-resort default.
const _activeOrg = (root) => {
  const orgs = _orgs(root)
  const pendingId = root?._pendingActiveOrgId
  if (pendingId) {
    const pending = orgs.find((o) => o.id === pendingId)
    if (pending) return pending
  }
  const activeId = _activeWsId(root)
  const w = _workspaces(root).find((x) => (x.id || x.workspace_id || x._id) === activeId)
  if (!w) return orgs[0] || null
  return orgs.find((o) => o.id === (w.org_id || w.organization)) || null
}

export const AppWorkspaceSwitcher = {
  hide: (el, s) => _orgs(s.root).length < 1,

  position: 'relative',
  flow: 'x',
  align: 'center left',

  Trigger: {
    tag: 'button',
    type: 'button',
    flow: 'x',
    align: 'center center',
    gap: 'V',
    padding: '- Z1',
    round: 'Z',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: 'Z2',
    fontWeight: '600',
    color: 'title',
    ':hover': { background: 'overlay' },
    onClick: (e, el, s) => {
      e.stopPropagation()
      s.root.update({ _wsMenuOpen: !s.root._wsMenuOpen })
    },
    Label: {
      tag: 'span',
      text: (el, s) => _activeOrg(s.root)?.name || 'Organization'
    },
    Chevron: { tag: 'span', fontSize: 'Y2', opacity: '.6', text: '▾' }
  },

  Menu: {
    // `display` is the gate (block when open, none when closed) — opacity-
    // toggling via reactive `opacity:` was not re-firing on state changes
    // in v3.14, so the menu stayed transparent even after _wsMenuOpen
    // flipped to true. `hide:` returns true for closed state and DOMQL
    // sets display:none predictably across renders.
    hide: (el, s) => !s.root?._wsMenuOpen,
    position: 'absolute',
    top: '110%',
    left: '0',
    zIndex: '200',
    minWidth: 'G',
    flow: 'y',
    padding: 'Z',
    round: 'Z',
    background: 'overlayStrong',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'line',
    boxShadow: '0 8px 24px var(--color-shadow)',

    onCreate: (el, s) => {
      el._close = (ev) => {
        if (!el.node?.parentElement) return
        if (el.node.parentElement.contains(ev.target)) return
        s.root.update({ _wsMenuOpen: false })
      }
      document.addEventListener('mousedown', el._close)
    },
    onRemove: (el) => {
      if (el._close) document.removeEventListener('mousedown', el._close)
    },

    Orgs: {
      flow: 'y',
      childExtends: 'AppOrgSwitcherItem',
      childrenAs: 'state',
      children: (el, s) => _orgs(s.root)
    },

    Divider: { height: '1px', background: 'line', margin: 'Z 0' },

    CreateOrg: {
      extends: 'Link',
      href: (el) => el.call('canvasUrl', '/organizations/new'),
      attr: { target: '_blank', rel: 'noopener' },
      padding: 'Z1 A',
      round: 'Y',
      fontSize: 'Z2',
      color: 'caption',
      textDecoration: 'none',
      ':hover': { background: 'overlay', color: 'title' },
      text: '+ Create organization'
    }
  }
}

// One org row. Activating an org means switching to its home workspace,
// which triggers RLS to re-scope every tenant-table query.
export const AppOrgSwitcherItem = {
  tag: 'button',
  type: 'button',
  flow: 'x',
  align: 'center left',
  gap: 'Z1',
  width: '100%',
  padding: 'Z1 A',
  round: 'Y',
  border: 'none',
  background: (el, s) => {
    const active = _activeOrg(s.root)
    return active && active.id === s.id ? 'overlay' : 'transparent'
  },
  cursor: 'pointer',
  fontSize: 'Z2',
  fontWeight: '500',
  color: 'title',
  textAlign: 'left',
  ':hover': { background: 'overlay' },

  Name: { tag: 'span', flex: '1', text: (el, s) => s.name },
  // Per-org attention badge. Reads from `root.orgNotifCount[orgId]` —
  // a simple count keyed by org id. Populated by whatever cross-org
  // notifications aggregator lands server-side (see NEEDED_FOR_SERVER
  // §I8); renders an empty string when the count is 0/undefined so the
  // slot stays invisible rather than showing stale layout.
  NotifBadge: {
    tag: 'span',
    fontSize: 'Y2',
    fontWeight: '700',
    color: 'white',
    background: 'red',
    round: 'C',
    padding: 'V Z',
    minWidth: 'Z1',
    textAlign: 'center',
    text: (el, s) => {
      const count = s.root?.orgNotifCount?.[s.id] || 0
      return count > 0 ? (count > 99 ? '99+' : String(count)) : ''
    },
    // Collapse the badge entirely when count is 0. Using visibility
    // keeps the flex gap consistent; display:none would cause layout
    // jitter on state updates in DOMQL v3.14.
    display: (el, s) => {
      const count = s.root?.orgNotifCount?.[s.id] || 0
      return count > 0 ? 'inline-block' : 'none'
    }
  },
  Role: {
    tag: 'span',
    fontSize: 'Y2',
    color: 'caption',
    opacity: '.7',
    text: (el, s) => s.role || 'member'
  },
  Check: {
    tag: 'span',
    color: 'green',
    text: (el, s) => {
      const active = _activeOrg(s.root)
      return active && active.id === s.id ? '✓' : ''
    }
  },

  onClick: async (e, el, s) => {
    e.stopPropagation()
    const active = _activeOrg(s.root)
    if (active && active.id === s.id) {
      // Already on this org — just close the menu, no work to do.
      s.root.update({ _wsMenuOpen: false })
      return
    }
    const home = _homeWorkspace(s, s.root)
    if (!home) {
      s.root.update({ _wsMenuOpen: false })
      console.warn('[AppOrgSwitcher] org has no home workspace:', s.id)
      return
    }
    const targetId = home.id || home.workspace_id || home._id
    // Optimistic UI: flip the dropdown's active state THIS tick so the
    // Trigger label and the row's checkmark/highlight reflect the user's
    // pick instantly. The "Switching to <org>…" overlay paints alongside
    // for slow round-trips. `_pendingActiveOrgId` is read by `_activeOrg`
    // and clears once the JWT refresh lands (or on failure below).
    s.root.update({
      _wsMenuOpen: false,
      _pendingActiveOrgId: s.id,
      _wsSwitching: s.name || 'workspace'
    })
    try {
      const result = await el.sdk.switchWorkspace(targetId)
      if (!result?.ok) {
        // Revert the optimistic pick — JWT didn't actually move.
        s.root.update({ _wsSwitching: null, _pendingActiveOrgId: null })
        const rawErr = result?.error?.message || result?.error || 'unknown error'
        console.warn('[AppOrgSwitcher] switch failed:', rawErr)
        // Surface the failure so the user understands why the dropdown
        // snapped back. The most common case is a server-side
        // membership mismatch (governance JWT lists a workspace the
        // Symbols API's `/auth/switch-workspace` endpoint disagrees
        // about) — translate that to an admin-actionable message.
        const isMembershipErr = /not a member of target workspace/i.test(String(rawErr))
        el.call('openNotification', {
          title: `Couldn't switch to ${s.name || 'workspace'}`,
          message: isMembershipErr
            ? "The server says you're not a member of that workspace. The org sidebar shows you as a member — that's a backend consistency bug. Ask an admin to re-add you, or contact support."
            : String(rawErr),
          type: 'error'
        })
        return
      }
      // The JWT refresh emitted by the federation triggers an
      // onAuthStateChange(TOKEN_REFRESHED) event which subscribeAuthSession
      // detects as a workspace change (active_workspace_id changed).
      // The onWorkspaceChange callback in app.js re-fetches all
      // workspace-scoped data and clears _wsSwitching + _pendingActiveOrgId
      // — no reload needed.
      //
      // If the onAuthStateChange event does not fire within 5 s (Supabase
      // WS not available, e.g. offline), fall back to a full reload so the
      // user is never left staring at the overlay indefinitely.
      clearWorkspaceSwitchFallback()
      _wsFallbackTimer = setTimeout(() => {
        _wsFallbackTimer = null
        if (s.root?._wsSwitching && typeof window !== 'undefined') {
          console.warn('[AppOrgSwitcher] onWorkspaceChange timeout — falling back to reload')
          window.location.reload()
        }
      }, 5000)
    } catch (err) {
      s.root.update({ _wsSwitching: null, _pendingActiveOrgId: null })
      const msg = err?.message || String(err) || 'unknown error'
      console.warn('[AppOrgSwitcher] switch threw:', msg)
      el.call('openNotification', {
        title: `Couldn't switch to ${s.name || 'workspace'}`,
        message: msg,
        type: 'error'
      })
    }
  }
}

// Full-viewport transition overlay shown while the workspace switch is in
// flight. Cleared by app.js's _onWorkspaceChange once all workspace-scoped
// data has been reloaded in-place. Driven by `root._wsSwitching`.
export const AppWorkspaceSwitchOverlay = {
  hide: (el, s) => !s.root?._wsSwitching,
  position: 'fixed',
  inset: '0',
  zIndex: '1000',
  background: 'black.85',
  backdropFilter: 'blur(8px)',
  align: 'center center',
  flow: 'column',
  gap: 'A',
  Label: {
    tag: 'p',
    color: 'title',
    fontSize: 'Z1',
    fontWeight: '600',
    margin: '0',
    text: (el, s) => `Switching to ${s.root?._wsSwitching || 'workspace'}…`
  }
}
